import { Injectable } from '@angular/core';
import {
  HttpErrorResponse,
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
} from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { AuthenticationService } from '../services/authentication.service';
import { Router } from '@angular/router';
import { NotificationService } from '../notification/notification.service';
import { NotificationType } from '../enum/notification-type.enum';
import { MatDialog } from '@angular/material/dialog';

/**
 *<h3> AuthInterceptor is an Angular HttpInterceptor that is responsible for adding the JWT token to the headers of all authenticated API requests.
 * <p>It will automatically ignore requests to the unauthenticated endpoints: login, register, and reset-password.
 * @Injectable() decorator is used to make this class available to be injected as a dependency, by the Angular dependency injection system.
 * @class AuthInterceptorThis class represents the AuthInterceptor that implements the HttpInterceptor interface, it means this class can be used to intercept HTTP requests and responses.
 * @implements {HttpInterceptor} This class implements the HttpInterceptor interface, it means this class can be used to intercept HTTP requests and responses.
 *
 */
@Injectable()
export class AuthInterceptor implements HttpInterceptor {
  private unauthenticatedEndpoints: string[];

  constructor(
    private authenticationService: AuthenticationService,
    private router: Router,
    private notification: NotificationService,
    private dialog: MatDialog
  ) {
    this.unauthenticatedEndpoints = [
      `${this.authenticationService.host}/auth/login`,
      `${this.authenticationService.host}/auth/register`,
      `${this.authenticationService.host}/auth/password-reset/request`,
      `${this.authenticationService.host}/auth/password-reset/update`,
      `/links`,
    ];
  }
  /**
   *
   *<h3> unauthenticatedEndpoints is an array of endpoints that do not require authentication.
   * @private
   * @type {string[]}
   * @memberof AuthInterceptor
   */

  /**
   *
   *<h3> Creates an instance of AuthInterceptor.
   *@param {AuthenticationService} authenticationService the Service that provides authentication functionality for the application
   *@memberof AuthInterceptor
   */

  /**
   * Intercepts the Http request and adds an authorization header if the request's url is not an unauthenticated endpoint.
   * @param httpRequest the Http request to be intercepted
   * @param httpHandler
   * @returns the Http request with an added authorization header if the request's url is not an unauthenticated endpoint.
   */
  intercept(httpRequest: HttpRequest<any>, httpHandler: HttpHandler): Observable<HttpEvent<any>> {
    if (this.unauthenticatedEndpoints.some((endpoint) => httpRequest.url.includes(endpoint))) {
      return httpHandler.handle(httpRequest);
    }
    if (!this.isUserLoggedIn()) {
      this.dialog.closeAll();

      this.router.navigate(['/auth/login']).then(() => {
        this.notification.notify(
          NotificationType.WARNING,
          'Vous devez vous connecter pour accéder à cette page',
          ''
        );
      });
      return throwError(() => new Error('User is not logged in.'));
    }
    this.authenticationService.loadToken();
    const token = this.authenticationService.getToken();
    const request = httpRequest.clone({ setHeaders: { Authorization: `Bearer ${token}` } });
    return httpHandler.handle(request).pipe(
      catchError((error: HttpErrorResponse) => {
        if (error.status === 401) {
          this.authenticationService.clearSession();
          this.dialog.closeAll();
          this.router.navigate(['/auth/login']).then(() => {
            this.notification.notify(
              NotificationType.WARNING,
              'Votre session a expiré, veuillez vous reconnecter',
              ''
            );
          });
        }
        return throwError(() => error);
      })
    );
  }

  private isUserLoggedIn(): boolean {
    return this.authenticationService.isLogin();
  }
}
